import { Button, ErrorBoundary, Heading, Icon, Input, Text, VStack } from '@genial/components';
import { Spacing, Typography } from '@genial/design-tokens';
import { Utils, useBreakpointValue } from '@genial/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigation } from '@react-navigation/native';
import { Pressable } from 'native-base';
import React, { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import * as zod from 'zod';

import { SignedOutFormContainer } from '../../../components/SignedOutFormContainer';
import { SignedOutHeader } from '../../../components/SignedOutHeader';
import { SignedOutWrapper } from '../../../components/SignedOutWrapper';
import { SignedOutNavigationScreenNavigationProp } from '../../../routes/types';
import { useSessionActions } from '../../../store/session/actions';

import { passwordValidations, validatePassword, validationIndicators } from './utils';

const userSchema = zod.object({
  email: zod.string().email(),
  cpf: zod.string().refine(Utils.Validate.validateCpf, 'CPF inválido'),
});

const passwordSchema = zod
  .object({
    password: zod.string().refine(validatePassword, 'Senha inválida'),
    confirmPassword: zod.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    path: ['confirmPassword'],
    message: passwordValidations.passwordsMatch,
  });

const validationSchema = zod.intersection(userSchema, passwordSchema);

type CreatePasswordFormData = zod.infer<typeof validationSchema>;

export const ForgotPasswordCreate: React.FC = () => {
  const navigation = useNavigation<SignedOutNavigationScreenNavigationProp>();
  const [isCreatingPassword, setIsCreatingPassword] = React.useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = React.useState(false);
  const [password, setPassword] = React.useState<string>('');
  const [confirmPassword, setConfirmPassword] = React.useState<string>('');
  const { recoverPasswordReset } = useSessionActions();
  const queryParams = new URLSearchParams(window.location.search);
  const isMobile = useBreakpointValue({ base: true, lg: false });
  const token = queryParams.get('token');
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<CreatePasswordFormData>({
    resolver: zodResolver(validationSchema),
    mode: 'all',
  });

  const handleCreatePassword = (data: CreatePasswordFormData) => {
    setIsCreatingPassword(true);

    if (!token) {
      return;
    }

    recoverPasswordReset(data.email, data.cpf, token, data.password, data.confirmPassword)
      .then(() => {
        setTimeout(() => {
          navigation.navigate({ key: 'SignIn' });
        }, 1000);
      })
      .finally(() => {
        setIsCreatingPassword(false);
      });
  };

  const handleGoBack = useCallback(() => {
    if (navigation.canGoBack()) {
      return navigation.goBack();
    }

    navigation.navigate({ key: 'SignIn' });
  }, [navigation]);

  const handleChangePassword = useCallback((newPassword: string) => {
    setPassword(newPassword);
  }, []);

  const handleChangeConfirmPassword = useCallback((newConfirmPassword: string) => {
    setConfirmPassword(newConfirmPassword);
  }, []);

  const handleBackToHome = () => {
    navigation.navigate({ key: 'SignIn' });
  };

  if (token === null) {
    return (
      <SignedOutWrapper>
        <SignedOutFormContainer>
          <ErrorBoundary
            errorMessage="Token inválido"
            refreshText="Voltar a Home"
            onRefresh={handleBackToHome}
          />
        </SignedOutFormContainer>
      </SignedOutWrapper>
    );
  }

  return (
    <SignedOutWrapper>
      <SignedOutHeader isMobile={isMobile} />
      <SignedOutFormContainer>
        <Heading
          testId="singin-title"
          size="h1"
          color="neutral.black"
          fontSize={{ sm: Typography['fontsize.xxl'], md: Typography['fontsize.display'] }}
          variant="semibold">
          Redefinição de senha
        </Heading>
        <Text variant="bold" fontSize={Typography['fontsize.lg']}>
          Crie sua nova senha de acesso à conta.
        </Text>
        <VStack w="full" space={Spacing['stack.nano']}>
          <Text variant="semibold" color="neutral.black" fontSize={Typography['fontsize.md']}>
            E-mail
          </Text>
          <Input
            control={control}
            placeholder="Digite seu e-mail"
            keyboardType="email-address"
            name="email"
            autoComplete="email"
            variant="filled"
            error={errors.email}
            isDisabled={isCreatingPassword}
          />
        </VStack>
        <VStack w="full" space={Spacing['stack.nano']}>
          <Text variant="semibold" color="neutral.black" fontSize={Typography['fontsize.md']}>
            CPF
          </Text>
          <Input
            control={control}
            placeholder="Digite seu CPF"
            keyboardType="number-pad"
            name="cpf"
            autoComplete="off"
            variant="filled"
            error={errors.cpf}
            isDisabled={isCreatingPassword}
          />
        </VStack>
        <VStack w="full" space={Spacing['stack.nano']}>
          <Text variant="semibold" color="neutral.black" fontSize={Typography['fontsize.md']}>
            Senha
          </Text>
          <Input
            control={control}
            placeholder="Digite sua senha"
            secureTextEntry={!isPasswordVisible}
            name="password"
            autoComplete="password"
            variant="filled"
            error={errors.password}
            isDisabled={isCreatingPassword}
            onChange={(e) => handleChangePassword(e.nativeEvent.text)}
            inputRightElement={
              <Pressable
                onPress={() => {
                  setIsPasswordVisible(!isPasswordVisible);
                }}
                pr={2}
                testID="button-clear-input">
                <Icon name={isPasswordVisible ? 'Visibility' : 'VisibilityOff'} size="xxs" />
              </Pressable>
            }
          />
        </VStack>
        <VStack w="full" space={Spacing['stack.nano']}>
          <Text variant="semibold" color="neutral.black" fontSize={Typography['fontsize.md']}>
            Confirma nova Senha
          </Text>
          <Input
            control={control}
            placeholder="Digite sua senha"
            secureTextEntry={!isPasswordVisible}
            name="confirmPassword"
            autoComplete="password"
            variant="filled"
            error={errors.confirmPassword}
            isDisabled={isCreatingPassword}
            onChange={(e) => handleChangeConfirmPassword(e.nativeEvent.text)}
            inputRightElement={
              <Pressable
                onPress={() => {
                  setIsPasswordVisible(!isPasswordVisible);
                }}
                pr={2}
                testID="button-clear-input">
                <Icon name={isPasswordVisible ? 'Visibility' : 'VisibilityOff'} size="xxs" />
              </Pressable>
            }
          />
        </VStack>
        <Button onPress={handleSubmit(handleCreatePassword)} disabled={isCreatingPassword}>
          Enviar
        </Button>
        <VStack w="full" space={Spacing['stack.nano']}>
          <Text variant="semibold" fontSize={Typography['fontsize.md']}>
            Requisitos
          </Text>
          {validationIndicators(password, confirmPassword)}
        </VStack>
        <Button onPress={handleGoBack} variant="secondary" disabled={isCreatingPassword}>
          Voltar
        </Button>
      </SignedOutFormContainer>
    </SignedOutWrapper>
  );
};
